import { useMemo } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { getLocalDateString } from '../../utils/dateUtils';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

export default function MonthCalendar({
  currentMonth,
  onMonthChange,
  selectedDate,
  onDateSelect,
  meetingCounts = {}
}) {
  const today = getLocalDateString(new Date());

  // Get calendar grid data
  const calendarDays = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();

    // First day of month
    const firstDay = new Date(year, month, 1);
    const startingDayOfWeek = firstDay.getDay();

    // Days in month
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    // Days from previous month to show
    const daysFromPrevMonth = startingDayOfWeek;
    const prevMonth = new Date(year, month, 0);
    const prevMonthDays = prevMonth.getDate();

    const days = [];

    // Previous month days
    for (let i = daysFromPrevMonth - 1; i >= 0; i--) {
      const day = prevMonthDays - i;
      const date = new Date(year, month - 1, day);
      days.push({
        date: getLocalDateString(date),
        day,
        isCurrentMonth: false
      });
    }

    // Current month days
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      days.push({
        date: getLocalDateString(date),
        day,
        isCurrentMonth: true
      });
    }

    // Next month days to fill grid (6 rows x 7 days = 42)
    const remainingDays = 42 - days.length;
    for (let day = 1; day <= remainingDays; day++) {
      const date = new Date(year, month + 1, day);
      days.push({
        date: getLocalDateString(date),
        day,
        isCurrentMonth: false
      });
    }

    return days;
  }, [currentMonth]);

  const handlePrevMonth = () => {
    const newDate = new Date(currentMonth);
    newDate.setMonth(newDate.getMonth() - 1);
    onMonthChange(newDate);
  };

  const handleNextMonth = () => {
    const newDate = new Date(currentMonth);
    newDate.setMonth(newDate.getMonth() + 1);
    onMonthChange(newDate);
  };

  const handleToday = () => {
    onMonthChange(new Date());
    onDateSelect(today);
  };

  return (
    <div className="bg-surface-card border border-border rounded-xl p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <button
            onClick={handlePrevMonth}
            className="p-2 rounded-lg hover:bg-surface-hover transition-colors"
          >
            <ChevronLeft size={16} className="text-text-secondary" />
          </button>
          <h3 className="text-sm font-semibold text-text-primary min-w-[140px] text-center">
            {MONTHS[currentMonth.getMonth()]} {currentMonth.getFullYear()}
          </h3>
          <button
            onClick={handleNextMonth}
            className="p-2 rounded-lg hover:bg-surface-hover transition-colors"
          >
            <ChevronRight size={16} className="text-text-secondary" />
          </button>
        </div>
        <button
          onClick={handleToday}
          className="text-xs text-accent hover:text-accent-bright transition-colors"
        >
          Today
        </button>
      </div>

      {/* Day headers */}
      <div className="grid grid-cols-7 gap-1 mb-1">
        {DAYS.map(day => (
          <div
            key={day}
            className="text-center text-[10px] font-medium text-text-tertiary py-1"
          >
            {day}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-1">
        {calendarDays.map(({ date, day, isCurrentMonth }, index) => {
          const isToday = date === today;
          const isSelected = date === selectedDate;
          const meetingCount = meetingCounts[date] || 0;
          const hasMeetings = meetingCount > 0;

          return (
            <button
              key={index}
              onClick={() => onDateSelect(date)}
              className={`
                aspect-square rounded-lg text-xs flex flex-col items-center justify-center
                transition-all duration-150 relative
                ${!isCurrentMonth ? 'text-text-muted' : ''}
                ${isToday && !isSelected ? 'bg-accent text-white font-semibold' : ''}
                ${isSelected ? 'ring-2 ring-accent ring-offset-1' : ''}
                ${isCurrentMonth && !isToday && !isSelected ? 'text-text-primary hover:bg-surface-hover' : ''}
                ${hasMeetings && isCurrentMonth && !isToday ? 'font-medium' : ''}
              `}
            >
              <span>{day}</span>
              {hasMeetings && (
                <div className="flex gap-0.5 mt-0.5">
                  {meetingCount <= 3 ? (
                    [...Array(meetingCount)].map((_, i) => (
                      <div
                        key={i}
                        className={`w-1 h-1 rounded-full ${isToday ? 'bg-white/80' : 'bg-accent'}`}
                      />
                    ))
                  ) : (
                    <span className={`text-[8px] ${isToday ? 'text-white/80' : 'text-accent'}`}>
                      {meetingCount}
                    </span>
                  )}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
